"use client";

import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { isEditing } from "@builder.io/sdk-react";
import Script from "next/script";

import { useConfig } from "@jti/app-context/useConfig";
import type { liveChat } from "@jti/fe-clients";
import { AGE_CONFIRMED_EVENT, isSoftAgeVerificationConfirmed } from "@jti/fe-common/utils";
import { getUser } from "@jti/rest-api";

import { getSavLiveChatConfig } from "@/app/_internal/getSavLiveChatConfig";
import { useLocale } from "@/context/LocaleContext";

import type { liveChatInputs } from "./LiveChat.builder";

export type LiveChatProps = BuilderComponentProps<typeof liveChatInputs>;

declare global {
  interface Window {
    SN_CSM_EC?: liveChat.LiveChatObject;
  }
}

const initChatWidget = ({ languageCode, moduleId }: { languageCode: string; moduleId: string }): void => {
  const getTokenCallBack = () => {
    const user = getUser();
    Promise.resolve(user.ciam_token || null);
  };

  if (typeof window.SN_CSM_EC === "undefined") {
    return;
  }

  const { uuid } = getUser();
  const contextObject = window.SN_CSM_EC.loadEMFeature();
  contextObject.openOnLoad = false; // default is true, so we need to overwrite to keep the chat closed
  contextObject.params.language = languageCode;
  contextObject.params.sysparm_vaMarket = languageCode;

  if (uuid) {
    contextObject.params.sysparm_consumer_cas_uuid = uuid;
  }

  window.SN_CSM_EC.init({
    moduleID: moduleId,
    loadFeature: contextObject,
    tokenCallBack: getTokenCallBack,
    lang: languageCode,
  });
};

export const LiveChat: FC<LiveChatProps> = ({ enabled, configuration: { scriptSrc, languageCode, moduleId } }) => {
  const { currentLocale } = useLocale();

  const [isChatAvailable, setIsChatAvailable] = useState<boolean>(false);
  const [isAgeConfirmed, setIsAgeConfirmed] = useState<boolean>(false);

  useEffect(() => {
    const checkAgeConfirmation = () => {
      setIsAgeConfirmed(isSoftAgeVerificationConfirmed());
    };

    checkAgeConfirmation();

    window.addEventListener(AGE_CONFIRMED_EVENT, checkAgeConfirmation);

    return () => {
      window.removeEventListener(AGE_CONFIRMED_EVENT, checkAgeConfirmation);
    };
  }, []);

  useEffect(() => {
    const fetchSavLiveChatConfigData = async () => {
      const requiresSav = await getSavLiveChatConfig({ locale: currentLocale });

      if (requiresSav && isAgeConfirmed) {
        setIsChatAvailable(true);
      }

      if (requiresSav && !isAgeConfirmed) {
        setIsChatAvailable(false);
      }

      if (!requiresSav) {
        setIsChatAvailable(true);
      }
    };

    fetchSavLiveChatConfigData();
  }, [currentLocale, isAgeConfirmed]);

  const { liveChatLazyLoading: lazyLoad = true } = useConfig();

  const handleScriptLoad = useCallback(() => {
    initChatWidget({ languageCode, moduleId });
  }, [languageCode, moduleId]);

  const strategy = lazyLoad ? "lazyOnload" : "afterInteractive";

  return (
    <>
      {isEditing() && "LiveChat authoring placeholder"}
      {!isEditing() && enabled && isChatAvailable && (
        <Script src={scriptSrc} strategy={strategy} onLoad={handleScriptLoad}></Script>
      )}
    </>
  );
};
